/* ============================================
   QUOTE DASHBOARD — Sample Tracking + Commission cards
   Asset: dashboard-sample-commission.js

   Extracted from dashboard-main.js on 2026-05-23 as Phase B.2.d of the
   dashboard refactor. Bundles three sibling cards that all live in the
   same column-3 region of the detail panel:

     - Quote-level Sample Tracking (admin-only) — separate from the
       LEAD-level Shipment Tracking card that moved with B.2.b. This is
       the card that renders on regular NEW LEAD / QUOTED / SOLD orders
       when admin records a sample shipment attached to them.
     - Commission card (offline / non-online orders) — rep + CFA
       commission lines, lead source, band, rate, GP-of-Goods, referral
       split. Three render modes (rep earns / rep doesn't but has
       referral / rep doesn't + no referral). Linked-children skip.
     - Commission Code Attribution (online orders, discount-code path)
       — Phase 3.5 Phase 1.5 card for SHOP-XXXX orders attributed via a
       registered discount code at checkout. Editable lead-source
       dropdown for admin on Online/All tabs; read-only otherwise.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildSampleTrackingCard(q, ctx) -> html string
       Self-gates on isAdmin + !isSample + !isDeadLead +
       !q.sampleTracking.number (i.e. lead-level shipment-tracking card
       isn't already rendering) + vendor list match (ECORE/ECORE
       ATHLETIC/CPT/TURF-family) when a vendor list exists. PENDING deals
       qualify (no vendor list → vendor gate skipped); their tracking lives
       on the pending:{ref} record (q.quoteSampleTracking). ctx supplies
       row-state flags (isSample, isPend, isDeadLead) computed in
       buildDetailHtml. Returns '' when gate fails.

     buildCommissionCard(q) -> html string
       Self-gates on q.source !== 'online' && !q.linkedParentRef &&
       (rep earns OR has referral) && NOT a PENDING deal (Ryan 2026-09-23).
       Returns '' when gate fails.

     buildCommissionCodeAttribution(q) -> html string
       Self-gates on q.source === 'online' && attribution.method ===
       'discount-code'. Returns '' when gate fails.

   Init contract (RQD.initSampleCommission called once by main.js at boot):
     esc, formatDate, money, detailRow, capitalize — function declarations
       in main, safe to pass by reference per the init-deps-var-hoisting-
       trap memo. capitalize lives at ~L14960 (well after this init), but
       function decls are fully hoisted.
     getIsAdmin — closure-getter. isAdmin is a `var` in main that mutates
       after Auth.getSession() resolves (~L91).
     getCurrentSource — closure-getter. currentSource is a `var` in main
       (default 'offline', reassigned on source-picker click) — the Code
       Attribution card's read-only-vs-editable branch reads it.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  // Injected by RQD.initSampleCommission (from main.js boot)
  var esc, formatDate, money, detailRow, capitalize, getIsAdmin, getCurrentSource;

  // -------------------------------------------------------------------
  // Sample Tracking card (quote-level)
  // -------------------------------------------------------------------
  function buildSampleTrackingCard(q, ctx) {
    ctx = ctx || {};
    var isSample = !!ctx.isSample;
    var isPend = !!ctx.isPend;
    var isDeadLead = !!ctx.isDeadLead;

    // Quote-level Sample Tracking — admin-only. Lets admin record a sample
    // shipment attached to a regular NEW LEAD / QUOTED / in-progress /
    // SOLD order (NOT a sample-request lead) so the rep can see the
    // shipped state in the To Do column. Card is collapsible; defaults to
    // collapsed when no tracking exists yet and expanded when tracking is
    // on file. Hidden on sample / dead — those states have their own UX or
    // don't qualify. Admin-only is the gate; admins only stamp tracking when
    // they've actually shipped a sample.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-25) — PENDING deals now QUALIFY:
       admin can send/log/notify a sample before a quote is built. Pending has no
       vendor list, so the vendor gate below is skipped (_qstHasVendorList=false)
       and the card shows on every pending. Tracking + notify state live on the
       pending:{ref} record (worker: /quote-sample-tracking-save + /notify-customer-
       samples), surfaced as q.quoteSampleTracking / q.sampleNotifiedAt. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Sample shipments only happen for the
       flooring/turf vendors we stock samples of: ECORE, ECORE ATHLETIC,
       CPT, and any TURF-family vendor (Synlawn, ProGreen, etc. — name
       contains "TURF"). Gate the card on at least one order vendor
       matching that list so it stops appearing on orders for vendors
       that don't ship samples (treadmills, racks, etc.). Match is
       case-insensitive on the trimmed vendor name. */
    var _qstSampleVendors = ['ECORE', 'ECORE ATHLETIC', 'CPT'];
    var _qstOrderVendors = (q.financials && Array.isArray(q.financials.vendors)) ? q.financials.vendors : [];
    var _qstVendorOk = _qstOrderVendors.some(function (v) {
      var V = String(v || '').toUpperCase().trim();
      if (!V) return false;
      if (_qstSampleVendors.indexOf(V) !== -1) return true;
      if (V.indexOf('TURF') !== -1) return true;
      return false;
    });
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Lead-state rows
       (NEW LEAD, OFF HOLD, etc.) ship to the dashboard without a
       q.financials object, so _qstVendorOk is always false on them and
       the card was incorrectly hidden. The vendor gate was added
       2026-05-14 to suppress the card on QUOTED+ orders for vendors
       that don't ship samples (treadmills, racks) — it shouldn't apply
       to lead rows where the vendor isn't known yet and admin is
       deciding ad-hoc whether to send a sample. Skip the vendor gate
       when no vendor list is present. */
    var _qstHasVendorList = _qstOrderVendors.length > 0;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — !isOnHold gate
       removed so admin keeps card access on rows that flip back to
       ON HOLD after sample tracking was already saved. Status change
       doesn't undo a real shipment — sample still needs visibility +
       edit. Also suppress when the lead-level Shipment Tracking card
       is already rendering (q.sampleTracking.number present) so an
       ex-sample-source held lead doesn't get two parallel cards
       editing different stores. */
    var _qstHasLeadTracking = !!(q.sampleTracking && q.sampleTracking.number);
    var _qstShow = getIsAdmin() && !isSample && !isDeadLead && !_qstHasLeadTracking && (!_qstHasVendorList || _qstVendorOk);
    if (!_qstShow) return '';

    var html = '';
    var qst = q.quoteSampleTracking || {};
    var qstCarrier = qst.carrier ? esc(qst.carrier) : '';
    var qstNumber = qst.number ? esc(qst.number) : '';
    var qstUrl = qst.url ? esc(qst.url) : '';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — split in two. `_qstStamp` is the text, which
       now rides INSIDE the tracking summary as its own row (see buildSampleTrackingView).
       `qstMeta` survives only as the fallback for the no-summary path — a card whose view module
       has not attached still shows the stamp somewhere rather than dropping it silently. */
    var _qstStamp = (qst.savedBy && qst.savedAt)
      ? esc(qst.savedBy) + ' · ' + esc(formatDate(qst.savedAt) || qst.savedAt)
      : '';
    // Plain .rqd-detail__arap card — applyCollapseState() auto-wires
    // the collapse/expand affordance (adds .rqd-detail__arap--collapsible,
    // injects the +/− chevron, hooks the click on the section title).
    // The shouldAutoCollapse('sample-tracking', q) branch returns true
    // when no tracking is on file → card defaults collapsed.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE CARD DECLARES ITS COLLAPSE KEY NOW,
       AND THAT IS WHAT LETS THE HEADER CARRY A ✓.

       The note that stood here said the title text "must stay stable across renders (sectionSlug
       reads textContent to derive the collapse key) — no dynamic ✓ suffix inline", and that was
       true of a card that did not declare one: `sectionIdFor` falls back to slugging the title's
       textContent, so "SAMPLES ✓" and "SAMPLES" are two different cards as far as
       `collapsedSections` is concerned. But its FIRST branch reads `card.dataset.sectionId`, and
       its own comment allows for "any summary a declaring card put there" — the freight card has
       rendered `FREIGHT (1/1) ✓` this way since 2026-08-29.

       So the key is declared, and it is declared as `samples` UNCONDITIONALLY. Previously it
       tracked the title, which varies with `_qsLeadOwns` — a key that changes with the deal, kept
       working only because `shouldAutoCollapse` answers to both spellings. One stable declared key
       removes that coupling entirely; the branch still accepts both, so nothing else has to move.

       Ryan: *"Include a checkmark next to the SAMPLES header when a sample has been sent out and
       delivered so at a glance reps can see that samples were involved on the deal."*
       ⛔ SENT **AND** DELIVERED, not either. `sampleTracking.delivery` is stamped by the FedEx
       sweep (`lib/sample-shipments.js`) with `status:'DL'` — RW-1309-A carries
       `deliveredAt: 2026-08-16`. A tick on "we posted it" would say the thing arrived when nobody
       knows that yet, which is the one claim this mark must never make. */
    var _qsDelivery = (qst && qst.delivery) || null;
    var _qsDelivered = !!(_qsDelivery && (_qsDelivery.status === 'DL' || _qsDelivery.deliveredAt));
    var _qsWentOut = !!(qst && qst.number) || !!q.sampleNotifiedAt;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE TICK MEANS "SAMPLES WERE ORDERED" NOW.
       Ryan: *"there should be a checkmark on the 'SAMPLES' header like we get on other cards after
       completed actions have been done, so at a glance we can look at it and know samples were
       ordered."* The 08-30 rule (sent AND delivered) was about the box arriving; the question the
       header answers today is whether the request went to ECORE. Delivery still ticks it too. */
    var _qsReqStamp = ((window.RQD && typeof RQD.sampleRequestFor === 'function') ? RQD.sampleRequestFor(q) : (q.quoteSampleRequest || q.sampleRequest)) || null;
    /* Ryan 2026-09-24: a request parked for the send window (`scheduledFor`, no `sentAt`) ticks it too -
       the lead card's rule, see `_srGone` there. */
    var _qsDone = !!(_qsReqStamp && (_qsReqStamp.sentAt || _qsReqStamp.scheduledFor)) || (_qsWentOut && _qsDelivered);
    html += '<div class="rqd-detail__arap" data-section-id="samples">';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — ⛔ THE CARD IS TITLED "Samples" NOW, AND THE
       RENAME IS LOAD-BEARING RATHER THAN COSMETIC.
       ─────────────────────────────────────────────────────────────────────────────────────────
       Ryan: *"if there's an order which didn't originate as a 'SAMPLE' lead but flooring has been
       quoted, there is still a 'SAMPLE TRACKING' card… add the additional buttons it's now
       missing that we can see when the deal has a 'SAMPLE' status."* Those buttons belong ABOVE
       the tracking fields — you choose the samples, then a number comes back — which is the order
       the lead-side card has always used (SAMPLES, then Shipment Tracking).

       ⛔ AND A SECOND `.rqd-detail__section-title` CANNOT SIMPLY BE INSERTED ABOVE THIS ONE.
       `applyCollapseState` (dashboard-order-cards.js) takes the FIRST `.rqd-detail__section-title`
       inside each `.rqd-detail__arap` for BOTH the collapse key and the chevron it injects. A
       "Samples" heading placed before "Sample Tracking" would silently re-key the card from
       `sample-tracking` to `samples` and hang the +/− on the wrong line — a change with no error
       and no obvious symptom beyond a card that stops remembering whether it was open.

       📌 So the outer title becomes the samples heading and tracking drops to a sub-heading. The
       key moves to `samples` deliberately, and `shouldAutoCollapse` was renamed with it — see the
       branch there for why a card with a LIST but no tracking must now stay open.
       ⚠️ `collapsedSections` is an in-memory var (dashboard-main.js), not storage, so the key
       change costs nobody a remembered state. */
    /* ⛔ AND IT DEFERS TO THE LEAD CARD RATHER THAN COMPETING WITH IT. Both blocks use
       `.rqd-lead-samples[data-ref]` — the single selector the picker repaints by — so if both drew
       for one ref, a save would update whichever came first in the DOM and leave the other showing
       a stale list. The rule is owned by `RQD.leadSamplesBlockRenders` (dashboard-lead-detail.js)
       and asked here rather than re-derived: a sample-request deal's samples belong to that card,
       every other deal's belong to this one.
       ⚠️ `ctx.isLeadRow` is the OTHER half — the lead detail has to be rendering at all for its
       block to be a possibility. It is computed once in buildDetailHtml and passed in, so this
       cannot drift from the condition that actually decides whether that card appears. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AN INHERITED CARD IS READ-ONLY, AND THAT IS
       THE WHOLE SAFETY OF THE CASCADE.
       ─────────────────────────────────────────────────────────────────────────────────────────
       `samplesOriginRef` is set by the worker's samples chain pass when THIS revision has no
       samples of its own and a sibling does — RW-1309-B showing the three ECORE samples that
       shipped under RW-1309-A. Ryan picked read-through over shared ownership deliberately:
       `resolveSampleRecord('RW-1309-B')` walks lead -> wm -> pending, finds no lead for -B, and
       would WRITE to `workstation_meta:RW-1309-B`. So Adjust here would not edit the list on
       screen — it would mint a SECOND list for one customer, with nothing saying which you were
       editing, and the next render would show that new one instead.

       (⚠️ SUPERSEDED 2026-09-06 — the controls now act on the ORIGIN ref; see the block under
       `_qsInherited` below. Kept because the hazard it names is still the reason for that design.)
       ⛔ SO EVERY WRITE AFFORDANCE GOES, not just Adjust: Request (emails ECORE about a list this
       revision does not own), the tracking form and its Save (would stamp -B's own record),
       Notify (would mail a customer from a revision that never shipped anything), and the ladder's
       Stop/Resume (those write per-ref stop keys that the owning revision's automation never
       reads). A control that silently acts on a different record than the one you are reading is
       worse than an absent one. */
    var _qsInherited = !!q.samplesOriginRef;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ AN INHERITING REVISION IS NO LONGER
       READ-ONLY: ITS CONTROLS ACT ON THE ORIGIN REF INSTEAD.
       ─────────────────────────────────────────────────────────────────────────────────────────
       JW-1273: the customer picked samples on the website (lead JW-1273-A), asked for a quote
       before ordering the box, and the quote was revised to -B. Nothing had shipped. The 08-30
       rule above then stripped Adjust, Request, Notify, the tracking form and both automation
       rows from -B — the only revision anybody was looking at — and printed "Sent on JW-1273-A"
       over a box that was never sent. Ryan: *"now it's time to order them the samples and the UI
       isn't allowing it on the deal."*

       The 08-30 hazard was real and is still honoured: a write keyed on -B would mint a second
       list on -B's own record. So nothing here writes to -B. Every sample control on an
       inheriting card carries `_qsRef` = the ORIGIN ref — the record that owns the list, and the
       ref every ladder job, tombstone, open-index entry and ECORE Project Name is keyed on. The
       worker's read-through cascade then paints the result back onto -B, exactly as it does today
       for a box RW-1309-A shipped. `RQD.mirrorSampleFields` keeps -B's row in memory in step
       between saves and the next refetch.

       ⚠️ `_qsInherited` is still computed: it drives the note below, which now says where the
       record lives rather than that the card is frozen. It gates nothing else. */
    var _qsRef = q.samplesOriginRef || q.reference;
    var _qsRefEsc = esc(_qsRef);
    var _qsAdmin = getIsAdmin();
    var _qsLeadOwns = !!ctx.isLeadRow
      && !!(window.RQD && typeof RQD.leadSamplesBlockRenders === 'function' && RQD.leadSamplesBlockRenders(q));
    /* ⛔ READ FROM WHERE THE PICKER WRITES. `resolveSampleRecord` (worker) is lead-first, so a deal
       that HAS a lead record stores its list on the lead and surfaces it as `leadSamples` — even
       when this card is the one drawing it, which is every rfq-sourced quoted deal. Reading
       `quoteSamples` unconditionally would show "None yet" on a deal whose list is sitting on its
       lead record, and the next save would look like it had done nothing. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ THE COMMENT ABOVE WAS RIGHT AND THE
       PREDICATE UNDER IT WAS NOT. It read `q.isLead ? leadSamples : quoteSamples`, and `q.isLead`
       is FALSE on exactly the deals the comment is about — "every rfq-sourced quoted deal". So
       RW-1309-A, whose two ECORE samples shipped and were delivered in August, rendered
       "None yet — use Adjust to build the list." Now asks the shared lead-first resolver, which
       mirrors the worker's record order instead of guessing from status. */
    var _qsRaw = (window.RQD && typeof RQD.samplesListFor === 'function')
      ? RQD.samplesListFor(q)
      : (Array.isArray(q.leadSamples) && q.leadSamples.length ? q.leadSamples
          : (Array.isArray(q.quoteSamples) ? q.quoteSamples : []));
    var _qsSamples = (Array.isArray(_qsRaw) ? _qsRaw : []).filter(function (s) { return s && String(s).trim(); });
    /* ⛔ THE TITLE NAMES WHAT THIS CARD ACTUALLY CONTAINS, which is not the same on every deal.
       When the lead card owns the samples block, all that is left here is tracking and the two
       automation rows — and calling that "Samples" put TWO cards titled "Samples" on one screen,
       the upper one holding the list and the lower one holding none of it. Verified on a rendered
       fixture (sample-request lead, promoted, no tracking) rather than reasoned about.
       ⚠️ SO THE COLLAPSE KEY VARIES TOO, and `shouldAutoCollapse` answers to BOTH `samples` and
       `sample-tracking` for that reason. A key that changes with the deal is fine — it is derived
       per render and `collapsedSections` is in-memory — but a key with no branch behind it would
       silently stop auto-collapsing, which is the failure that leaves every ECORE order expanded. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ AN INHERITED CARD SAYS SO, IN WORDS.
       Without this the card is indistinguishable from one whose samples were sent on this very
       revision — and a rep who cannot see why Adjust is missing will read the card as broken
       rather than as read-only. Naming the owning revision also tells them exactly where to go to
       change it, which is the only place it CAN be changed. */
    var _qsOriginNote = _qsInherited
      ? '<div class="rqd-sample-tracking__meta">Samples are recorded on ' + esc(q.samplesOriginRef)
        + ' · changes made here are saved there.</div>'
      : '';
    /* Through the SHARED helper, so the ✓ is the same span, the same glyph and the same
       `--done` modifier the freight, install and purchasing cards already use — a second
       hand-rolled tick is how two cards start disagreeing about what "done" looks like. */
    html += ((window.RQD && typeof RQD.sectionTitleHtml === 'function')
      ? RQD.sectionTitleHtml((_qsLeadOwns ? 'Sample Tracking' : 'Samples'), _qsDone)
      /* The fallback carries the done state too (2026-09-06) — a fallback that drops it would
         render a card with no ✓ the day the shared helper failed to load, and nobody would know. */
      : '<div class="rqd-detail__section-title' + (_qsDone ? ' rqd-detail__section-title--done' : '') + '">'
        + (_qsLeadOwns ? 'Sample Tracking' : 'Samples')
        + (_qsDone ? '<span class="rqd-detail__section-done" title="Sample request sent to ECORE">&#10003;</span>' : '')
        + '</div>');
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — the samples list + Adjust + Request, the
       three things this card was missing against the lead-side one.

       ⛔ THE CLASSES ARE THE LEAD CARD'S, REUSED RATHER THAN PARALLELED. `.rqd-sample-adjust` is
       what dashboard-sample-picker.js listens for (a delegated click anywhere in the document, so
       it needs no new wiring), and `.rqd-lead-samples[data-ref]` is the exact selector its
       `repaintCard` queries after a save. A second set of class names would mean a save that
       repaints one card and silently not the other, and two blocks of CSS to keep in step.

       📌 THE BLOCK RENDERS WITH AN EMPTY LIST, the rule PM14 gave the lead card for the same
       reason: a deal with no samples is precisely the one that needs somewhere to put Adjust, and
       gating on `length > 0` left it as the only deal that could never be given a list.
       ⚠️ Admin-only, matching Adjust and Request on the lead side. A rep sees the list and no
       buttons — the same thing they see there. */
    if (!_qsLeadOwns && (_qsSamples.length || _qsAdmin)) {
      /* Same ownership question as the list, same answer — the request stamp is written next to
         the list by `_recordRequest`, on the same record. Reading it from the other one would
         label the button "Request" on a deal ECORE has already been asked about. */
      var _qsReq = ((window.RQD && typeof RQD.sampleRequestFor === 'function')
        ? RQD.sampleRequestFor(q)
        : ((q.isLead ? q.sampleRequest : q.quoteSampleRequest))) || null;
      var _qsSent = !!(_qsReq && _qsReq.sentAt);
      /* Ryan 2026-09-24: scheduled ghosts too, as "⌛ Scheduled" - the lead card's `_srSched` / `_srGone`. */
      var _qsSched = !!(_qsReq && !_qsReq.sentAt && _qsReq.scheduledFor);
      var _qsGone = _qsSent || _qsSched;
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE — "Request more" once something on the list is unrequested. */
      var _qsNew = (_qsSent && window.RQD && typeof RQD.sampleNewCountFor === 'function') ? RQD.sampleNewCountFor(q) : 0;
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE GATE IS THE LEAD CARD'S, through the
         shared helpers, so the two surfaces cannot disagree about "ready". Guarded like every other
         cross-file helper here; without the helpers the button falls back to ungated, never to
         permanently disabled. */
      var _qsGate = (window.RQD && typeof RQD.sampleRequestGate === 'function' && typeof RQD.sampleRequestGateInputFor === 'function')
        ? RQD.sampleRequestGate(RQD.sampleRequestGateInputFor(q))
        : { ready: true, missing: [] };
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ THE LIST AND THE BUTTONS SHARE ONE BAND
         under the SAMPLES underline. Ryan: *"'None yet…' should be higher up, just under the
         underline above them, with the gap between the text and the line the same as the gap
         between the button and the line (like the Adjust button)."* The buttons used to sit on
         their own row and the list below that, so the first sample name (or "None yet") was a
         full button-height lower than the underline. `.rqd-sample-band` is a flex row: the list
         grows on the left, the actions park on the right, both 8px under the line. Held in a
         variable so the list can be emitted FIRST — the picker repaints `.rqd-lead-samples` by
         its data-ref, so DOM order is free to change. */
      var _qsActions = '';
      if (_qsAdmin) {
        _qsActions += '<div class="rqd-sample-request__actions">'
          + '<button type="button" class="rqd-sample-adjust rqd-invoice__link' + (_qsGone ? ' rqd-sample-adjust--sent' : '') + '" data-ref="' + _qsRefEsc + '"'
          +   ' title="Change which samples we ask ECORE to ship. Updates the list on this card, and the request email with it.">'
          /* Ryan 2026-09-06: "Add" until there is a list to adjust; the picker repaints it after a save. */
          +   (_qsSamples.length ? 'Adjust' : 'Add') + '</button>'
          /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ghosted once the request has gone, and
             still clickable, exactly as on the lead side. The two surfaces render the same control
             and must not differ; see the long note beside `_srBtn` in dashboard-lead-detail.js for
             why this is a status signal rather than a `disabled`. (2026-09-06: a NOT-READY deal is
             `disabled`, which is a different state from "sent" — see the gate helpers over there.) */
          + '</div>';
      }
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — Request is handed to the Job Information
         band below, beside the two fields that gate it: *"it needs to be down a bit lower so
         it's more intuitive that both of those things are requirements."* Adjust stays here. */
      var _qsRequestBtn = _qsAdmin ? (''
          + '<button type="button" class="rqd-sample-request rqd-invoice__link'
          +   (_qsGone && !(_qsNew > 0) ? ' rqd-sample-request--sent' : '')
          +   (_qsSched ? ' rqd-sample-request--scheduled' : '')
          +   (_qsNew > 0 ? ' rqd-sample-request--more' : '')
          +   (_qsGate.ready ? '' : ' rqd-sample-request--blocked') + '" data-ref="' + _qsRefEsc + '"'
          +   (_qsGate.ready ? '' : ' disabled')
          +   ' title="' + (!_qsGate.ready
                ? esc('Not ready to send: needs ' + _qsGate.missing.join(', ') + '.')
                : _qsNew > 0
                ? esc(_qsNew + ' new sample' + (_qsNew === 1 ? '' : 's') + ' on the list not yet requested. Opens a reply on the original request asking ECORE to ship them.')
                : _qsSched
                ? esc('Scheduled for ' + (formatDate(_qsReq.scheduledFor) || _qsReq.scheduledFor) + ' (the next send window). Click again only to replace the queued email.')
                : _qsSent
                ? esc('Already requested' + (_qsReq && _qsReq.sentAt ? ' on ' + (formatDate(_qsReq.sentAt) || _qsReq.sentAt) : '')
                  + '. Click again only if they lost it or the list has changed — the composer will ask you to confirm.')
                : 'Email ECORE\'s samples desk as Abby, asking them to ship these. Opens the composer so you can read it first.')
          +   '">'
          +   (_qsSched ? '⌛ Scheduled' : _qsSent ? (_qsNew > 0 ? 'Request more' : 'Sent') : 'Request') + '</button>'
      ) : '';
      /* ⛔ "None yet" SAYS WHICH EMPTY THIS IS. A deal waiting for a list and a list that failed
         to load look identical as a blank box, and only one of them is somebody's job. */
      html += '<div class="rqd-sample-band">'
        + '<div class="rqd-lead-samples" data-ref="' + _qsRefEsc + '" data-count="' + _qsSamples.length + '">'
        /* CUSTOM_SAMPLES2ND_BY_CLAUDE — the ONE list renderer (dashboard-lead-detail.js); today's joined
           lines on a first-shipment deal, a tag per sample once there is a second ask or box. */
        + ((window.RQD && typeof RQD.sampleListHtml === 'function')
            ? RQD.sampleListHtml(q, _qsSamples)
            : (_qsSamples.length
                ? _qsSamples.map(function (s) { return esc(String(s).trim()); }).join('<br>')
                : '<em style="color:var(--rq-text-secondary);">None yet</em>'))
        + '</div>'
        + _qsActions
        + '</div>'
        + _qsOriginNote
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-10) — PHASE 3: the boxes on this customer's
           OTHER grouped deals, through the shared helper the lead-side card uses, so one box
           cannot be described two ways on two surfaces. ⛔ PROSE AND A REF, NO CONTROLS — see the
           header on `sampleSiblingsHtml` for why a group is shown and never inherited.
           ⚠️ Outside `.rqd-lead-samples`, which the picker repaints from a save response that
           knows nothing about a sibling. */
        + ((window.RQD && typeof RQD.sampleSiblingsHtml === 'function') ? RQD.sampleSiblingsHtml(q) : '');
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — the gate's reason and the Project row, the
         lead card's own markup. ⛔ READ-ONLY ON AN INHERITED CARD: a write here would land on a
         revision that does not own the list (see `_qsInherited`), the same reason Adjust is absent. */
      /* The row is built from THIS row's (cascaded) values but saves under the origin ref, so it
         is handed a shallow copy whose `reference` is the origin — the one field the helper puts
         on the element. */
      /* JOB INFORMATION — a sub-section like Shipment Tracking below it (Ryan 2026-09-06); `sub`
         because this card's collapse rule exempts every section title unless it carries `--sub`. */
      /* SHIPPING ADDRESS (Ryan 2026-09-06) sits between the list and Job Information; saves under
         the origin ref on an inheriting card like everything else here. */
      if (window.RQD && typeof RQD.sampleShipToRowHtml === 'function') html += RQD.sampleShipToRowHtml(_qsInherited ? Object.assign({}, q, { reference: _qsRef }) : q, { sub: true });
      if (window.RQD && typeof RQD.sampleProjectRowHtml === 'function') html += RQD.sampleProjectRowHtml(_qsInherited ? Object.assign({}, q, { reference: _qsRef }) : q, { sub: true, requestBtnHtml: _qsRequestBtn });
      /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: the same block the lead card
         draws, `--sub` headings for this card's collapse rule, keyed on the origin ref. */
      if (window.RQD && typeof RQD.sampleSecondaryBandHtml === 'function') html += RQD.sampleSecondaryBandHtml(_qsInherited ? Object.assign({}, q, { reference: _qsRef }) : q, { sub: true });
    }
    /* ⚠️ Tracking is a SUB-heading now, not the card name — same words and same position relative
       to the list as the lead-side card, so the two read as one control in two places.
       ⛔ ONLY WHEN THERE IS A LIST ABOVE IT TO SEPARATE FROM. A heading earns its place by
       dividing two things; when the lead card owns the samples the card title is already
       "Sample Tracking", and emitting this as well stacked two near-identical headings over one
       widget. Suppressed, that deal gets back exactly the card it had before today. */
    /* ⛔ `--sub` IS NOT COSMETIC — IT IS WHAT LETS THIS HEADING COLLAPSE (Ryan 2026-08-25).
       `.rqd-detail__arap--collapsed > *:not(.rqd-detail__section-title)` exempts EVERY section
       title from the collapse, on the assumption that a card has exactly one and it is the card's
       own name. This card has two, so a collapsed Samples card went on printing SHIPMENT TRACKING
       underneath its own header and read as two cards fused into one. The marker class is what the
       collapse rule keys on to make an exception of the exception. */
    /* ⛔ DECLARED BEFORE THE MARKUP THAT PRINTS IT, and hoisted here on purpose. `var` hoists to
       `undefined` rather than throwing, so a button built 50 lines below the line that emits it
       renders the literal string "undefined" with no error anywhere. This file's sibling
       (dashboard-freight-cards.js) has been bitten by that exact shape twice — see the `_wsOfficial`
       note in the PM28 changelog — and moving Notify above the tracking block is precisely the edit
       that creates the opportunity. */
    var _qstNotified = !!q.sampleNotifiedAt;
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE (Ryan 2026-09-24) — second recipient: "Waiting on 2nd box" while the
       second set has no tracking number (the lead card carries the same label; the handler confirms). */
    var _qstPairWait = !_qstNotified && !!(window.RQD && typeof RQD.samplePairWaiting === 'function' && RQD.samplePairWaiting(q));
    var _qstNotifyBtn = '<button type="button" class="rqd-sample-notify rqd-invoice__link' + (_qstPairWait ? ' rqd-sample-notify--waiting' : '') + '" data-ref="' + _qsRefEsc + '"'
      + ' title="' + (_qstNotified
          ? 'Send the samples email to this customer again. They have already been notified once.'
          : _qstPairWait
          ? 'The second recipient\'s box has no tracking number yet. Abby\'s automatic notice waits for both; clicking here asks whether to send the buyer their own number now.'
          : 'Saves the tracking and opens the Notify Customer modal (Send Test or Send to Customer from there).')
      + '">' + (_qstNotified ? 'Re-Notify' : (_qstPairWait ? 'Waiting on 2nd box' : 'Notify Customer')) + '</button>';
    if (!_qsLeadOwns) html += '<div class="rqd-detail__section-title rqd-detail__section-title--sub" style="margin-top:18px;font-size:13px;">Shipment Tracking</div>';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ NOTIFY MOVES UP, UNDER ITS OWN DIVIDER.
       Ryan: *"I like how that card has the two dividing lines and the Adjust/Request buttons are
       directly below the top dividing line, but the Notify Customer button in the section below it
       isn't right below the dividing line, it's at the bottom. It should be moved up so it too is
       right below the dividing line also."*
       The divider is the section title's own `border-bottom`, so "right below it" means emitted
       immediately after the heading — the position Adjust/Request already hold under SAMPLES.
       ⚠️ It is rendered in ONE place now, for both states. It used to be emitted twice: inside the
       edit row when notified, and in a trailing `__actions` block when not — which is why it sat
       at the bottom on exactly the deals that had not been notified yet, i.e. the ones where it is
       the next thing to do. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ NOTIFY SHARES THE BAND WITH THE FIELDS.
       Ryan: *"the input fields… run the full width of the card… This will allow us to position the
       3 fields up higher so they're as high up as the 'Notify Customer' button is"* and *"[the
       edit row's] text input fields… with a save and cancel button… these should all be on the
       same row as the 'Notify Customer' button."* The tracking block (summary view, or the edit
       row once a cell is clicked) is the left half of a `.rqd-sample-band--tracking`; Notify is
       the right half, top-aligned 8px under the underline — the same band the samples list and
       Adjust / Request share above. The summary grid is capped in CSS so it no longer runs to the
       card's edge. Emitted AFTER the block so the DOM order is fields-then-button; `_stEnterEdit`
       and `paintSampleTrackingView` find the block by class, not by position. */
    var _qstNotifyRow = '<div class="rqd-sample-tracking__actions rqd-sample-tracking__actions--top">' + _qstNotifyBtn + '</div>';
    /* CUSTOM_SAMPLES2ND_BY_CLAUDE — earlier boxes above the live band, the same helper the lead card uses. */
    if (window.RQD && typeof RQD.priorShipmentsHtml === 'function') html += RQD.priorShipmentsHtml(q);
    html += '<div class="rqd-sample-band rqd-sample-band--tracking">';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-22) — Restructured to
       single-row layout matching the FULFILLMENT card's bucket rows.
       All controls (carrier, tracking #, URL, Save) live in one
       .rqd-sample-tracking__row with the FULFILLMENT-card scaled
       widths so the visual rhythm matches across cards. */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-23) — "sent" once the sample's gone
       out (customer notified OR tracking # on file) → fields + Notify button ghost
       into the status-tinted completed look. */
    var _qstSent = !!q.sampleNotifiedAt || !!(qst && qst.number);
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — the same summary the lead-side card got.
       Ryan: *"quote-side should be done now."* This card is the SAME control in a different
       place — three permanently-open inputs for three short facts a machine is about to fill in —
       and once the lead card became a read-at-rest summary, leaving this one as a form made the
       two disagree about what a tracking number looks like.

       ⛔ THE ROWS COME FROM RQD.buildSampleTrackingView, NOT FROM A COPY. The ellipsis span, the
       ↗ on the URL row only, the italic "Add …" prompt when a field is empty: decided once in
       dashboard-lead-detail.js and reused here. A parallel copy is how two cards drift into two
       appearances of the same three facts.
       ⚠️ Falls back to the plain input row if that module has not attached yet — a card with no
       summary and a visible form still WORKS, whereas a card with neither is a dead end. The
       `--summary` class is what hides the inputs, so it is only added when the view exists.
       ⚠️ `--notified` is driven by sampleNotifiedAt, NOT by `--sent` (which only means a tracking
       number exists and is true before anyone has been told). */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — the stamp is a ROW of the summary now, under
       Tracking URL, instead of an italic line hanging below the whole block. Ryan: *"the 'saved by
       RW – 8/14/2026' line can be moved under the Tracking URL line, and in the same UI format as
       that text too, instead of italicized on an island by itself."*
       ⚠️ Passed as text and rendered by the shared view builder, so the lead-side card gets the
       identical treatment the moment it passes one — rather than this card growing a fourth row
       the other one does not have. */
    var _qstView = (window.RQD && typeof RQD.buildSampleTrackingView === 'function')
      ? RQD.buildSampleTrackingView(qstCarrier, qstNumber, qstUrl, _qstStamp)
      : '';
    html += '<div class="rqd-sample-tracking rqd-quote-sample-tracking'
      + (_qstView ? ' rqd-sample-tracking--summary' : '')
      + (_qstSent ? ' rqd-sample-tracking--sent' : '')
      + (_qstNotified ? ' rqd-sample-tracking--notified' : '')
      + '" data-ref="' + _qsRefEsc + '" data-owner="wm">'
      + (_qstView ? '<div class="rqd-sample-tracking__view">' + _qstView + '</div>' : '')
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) - the EDIT ROW is omitted entirely on an
         inherited card, not merely hidden. Its Save posts to /quote-sample-tracking-save with
         THIS ref, which would stamp a tracking number onto a revision that never shipped
         anything -- and `--readonly` also drops the click-to-edit on the summary cells, whose
         whole purpose is to open this row. See the read-only note near _qsInherited. */
      + ('<div class="rqd-sample-tracking__row">'
      +   '<div class="rqd-carrier-ac rqd-sample-tracking__carrier-wrap">'
      +     '<input type="text" class="rqd-vendor-tracking__carrier rqd-sample-tracking__carrier" placeholder="Carrier..." autocomplete="off" maxlength="60" value="' + qstCarrier + '">'
      +     '<div class="rqd-carrier-ac__menu" role="listbox"></div>'
      +   '</div>'
      +   '<input type="text" class="rqd-sample-tracking__number" placeholder="Tracking #" value="' + qstNumber + '">'
      +   '<input type="text" class="rqd-sample-tracking__url" placeholder="Tracking URL" value="' + qstUrl + '">'
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-20) — ⛔ SAVE, WITHOUT TELLING THE CUSTOMER.
         Notify Customer was the only control on this row, and it saves as a side effect — so
         correcting a tracking number meant emailing someone about it. Ryan, on a box that landed
         six weeks ago: *"technically in this case I don't want to notify them … just update the
         record."* The comment in the notify handler has claimed this button existed since May;
         its CSS was still here. The button was not. */
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-06) — ⛔ SAVE IS THE NOTIFY BUTTON'S SHAPE AND
         COLOUR. Ryan: *"the 'Save' button has the wrong UI -- it's a round pill and it's permanently
         blue, even if the deal has a status of 'ON HOLD' (purple theme). Should be the rectangular
         shaped button with the rounded corners just like the 'Notify Customer' button is, same color
         and everything."* `.rqd-invoice__link` is that button — 112px at this card's scale, repainted
         per status through `--rqd-lf-*`. The hard-coded blue pill rules for this class are deleted in
         dashboard.css, not overridden, so nothing can out-specify the status colour. Cancel takes the
         same shape as the outline variant Stop already uses. */
      +   '<button type="button" class="rqd-quote-sample-tracking__save rqd-invoice__link" data-ref="' + _qsRefEsc + '" title="Record the carrier and tracking number on this deal. No email is sent.">Save</button>'
      +   (_qstView ? '<button type="button" class="rqd-sample-tracking__cancel rqd-invoice__link" title="Discard these edits and go back to the summary.">Cancel</button>' : '')
      + '</div>')
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ BOTH OLD NOTIFY SITES ARE GONE, not one.
         The button lived in TWO places gated on opposite halves of `_qstNotified` — inside the edit
         row when notified, in a trailing `__actions` block when not. Leaving either would put a
         second Notify on the card now that it renders once under the heading, and the trailing one
         is the very placement Ryan asked to move.
         ⚠️ `qstMeta` went with them: the stamp is a row of the summary above now. The fallback for
         a card with no summary rides `_qstStamp` into the else-branch below rather than trailing
         the block, so it cannot reappear as the island it used to be. */
      + (_qstView ? '' : (_qstStamp ? '<div class="rqd-sample-tracking__meta">Saved by ' + _qstStamp + '</div>' : ''))
      + '</div>';
    html += _qstNotifyRow + '</div>';   /* Notify at the band's right edge, then the band closes */
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-25) — THE TWO AUTOMATION ROWS, the last thing this
       card was missing against the lead-side one.

       ⛔ THE NOTICE ROW WOULD HAVE BEEN A LIE UNTIL TODAY, AND THAT IS WHY THE WORKER CHANGED IN
       THE SAME COMMIT. Abby's "your samples are on the way" email is armed by the tracking write —
       and `handleQuoteSampleTrackingSave` never armed it, while `scheduleSampleAutoNotify` refused
       anything that was not a lead. So on a quoted deal this row would have read "nothing queued"
       for ever, above a Stop button governing an automation that could not run. A control whose
       state never changes is worse than an absent one: somebody eventually trusts it.
       Both halves are fixed (lead-routes `_armSampleAutoNotify`, cron `resolveSampleRecord`), so
       the row now describes something real.

       ⚠️ THE FOLLOW-UP ROW ALREADY WORKED HERE and was simply never rendered — the quote path
       calls `noteSampleShipment(kind:'wm')`, and `_loadDeals` reads `quoteSampleTracking`, so the
       ladder has been arming on these deals all along with no way to see or stop it.

       ⛔ THE MARKUP IS THE LEAD CARD'S, TO THE ATTRIBUTE. `_ladderScan` hydrates
       `[data-state="pending"]`; the notice rides `data-state="child"` so ONE fetch fills both rows
       and is painted onto the notice via `previousElementSibling`. That is why the notice must be
       rendered IMMEDIATELY BEFORE the ladder row and why both keep these exact classes — the
       delegated Stop/Resume handlers in dashboard-main.js are document-wide and need no new
       wiring, but the sibling relationship is positional and silently stops painting if reordered.
       ⚠️ Admin-only, like everything else that can switch off a customer email. */
    /* ⛔ AND THESE DEFER TOO, for the same reason the samples block does. The lead card draws the
       identical two rows whenever ITS tracking block renders, and this card already suppresses
       itself on `isSample` and on a lead tracking number — so those two legs can never collide.
       The third can: a deal that was NOTIFIED and has since had its tracking cleared satisfies
       neither gate, and both cards drew. Two Stop buttons for one automation is a control whose
       state one copy does not know about, plus two identical hydrate fetches per expand.
       ⚠️ Found by rendering the fixture, not by reading the gates — the combination is narrow
       enough that it reads as impossible right up until it is on screen. */
    var _qsLeadLadder = !!ctx.isLeadRow
      && !!(window.RQD && typeof RQD.leadTrackingBlockRenders === 'function' && RQD.leadTrackingBlockRenders(q));
    /* Inherited: the Stop/Resume pair writes `sample-*-stopped:{ref}` for THIS ref, which the
       owning revision's automation never consults — a switch that appears to work and governs
       nothing. See the read-only note above. */
    if (getIsAdmin() && !_qsLeadLadder) {
      html += '<div class="rqd-sample-ladder rqd-sample-ladder--notice" data-ref="' + _qsRefEsc + '" data-state="child">'
        /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-30) — ⛔ IT CARRIES THE REAL SECTION-TITLE
           CLASS NOW, WHICH IS THE ONLY WAY IT CAN EVER BE BLUE. Ryan: *"the CUSTOMER NOTICE line
           and FOLLOW-UP EMAILS lines aren't formatted like the headers/sub-headers/text above them
           with the blue headers and all."*
           `.rqd-sample-ladder__label` set its own colour from `--rq-text-secondary`, and its
           comment claimed that made it match SHIPMENT TRACKING. It cannot: the section title's
           colour is not a token at all — it is ~38 per-status selector lists
           (`.rqd-detail--quoted .rqd-detail__section-title { color:#1e3a8a }` and siblings), and
           this class is in none of them. Wearing the class inherits every one of those rules, for
           every status, in both themes, permanently.
           ⛔ THE ELEMENT STAYS INSIDE THE ROW. The notice is painted via the ladder row's
           `previousElementSibling`, so hoisting the label out to its own heading div would insert
           an element between the two rows and silently stop that paint. The themed rules are
           descendant selectors, so a nested span inherits them without moving. */
        + '<span class="rqd-sample-ladder__label rqd-detail__section-title rqd-detail__section-title--sub">Customer notice</span>'
        + '<span class="rqd-sample-ladder__state">&hellip;</span>'
        /* `hidden` until the status says a notice is queued (Ryan 2026-09-06) — `_noticePaint` owns it. */
        + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-notice__stop" data-ref="' + _qsRefEsc + '" hidden title="Stop Abby from automatically emailing this customer that their samples shipped. Also refuses any future re-arm, so re-saving the tracking number cannot switch it back on.">Stop</button>'
        + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-notice__resume" data-ref="' + _qsRefEsc + '" hidden title="Allow the automatic notice again. This does NOT send or queue anything by itself.">Resume</button>'
        + '</div>';
      html += '<div class="rqd-sample-ladder" data-ref="' + _qsRefEsc + '" data-state="pending">'
        + '<span class="rqd-sample-ladder__label rqd-detail__section-title rqd-detail__section-title--sub">Follow-up emails</span>'
        + '<span class="rqd-sample-ladder__state">&hellip;</span>'
        + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-ladder__stop" data-ref="' + _qsRefEsc + '" title="Stop the automatic samples follow-up emails for this deal. Also refuses any future re-arm, so a later delivery scan cannot switch it back on.">Stop</button>'
        + '<button type="button" class="rqd-sample-ladder__btn rqd-invoice__link rqd-sample-ladder__resume" data-ref="' + _qsRefEsc + '" hidden title="Allow the automatic follow-ups again. This does NOT send or queue anything by itself.">Resume</button>'
        + '</div>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    html += '</div>';
    return html;
  }

  // -------------------------------------------------------------------
  // Commission card — offline / non-online orders
  // -------------------------------------------------------------------
  function buildCommissionCard(q) {
    var fin = q.financials || {};
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-09-23) — ⛔ NO COMMISSION CARD ON A PENDING DEAL. A
       pending has no items, no terms and no GP, so every line of the card read "—"; Ryan: *"we don't
       need it to exist on PENDING deals. Please eliminate it."* Self-gated here beside the online and
       linked-child gates, so the one-line caller in dashboard-main.js stays as it is. */
    if (q && (q.isPending || q.status === 'pending')) return '';

    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-22) — ⛔ A REPLACEMENT READS $0, AND IT HAS TO
       BE FORCED RATHER THAN COMPUTED.

       Ryan asked for the rep to keep this card on a replacement showing "$0 or whatever". The
       "whatever" is the problem: the normal path computes
         gpCalc = fin.gpOfGoods || (directTotal − costTotal)
       and a replacement is $0 revenue against a REAL supplier cost — so gpCalc is NEGATIVE and
       the card would tell a rep their commission on it was MINUS several hundred dollars.

       That is the exact thing this whole feature exists to prevent. Ryan's constraint when it
       was built: "reps commission does NOT take a hit from a claim that doesn't get paid out
       on. that remains unaffected." The worker already honours it — commissions.js returns []
       on an RPL- ref, so nothing is actually docked — but this card would have said otherwise
       on the one screen the rep reads.

       So the card renders, keeps the rep's third column, and states the invariant instead of
       doing arithmetic on a number that cannot mean anything here. */
    if (q && q.isReplacement) {
      return '<div class="rqd-detail__card">'
        + '<div class="rqd-detail__section-title">Commission</div>'
        + '<div class="rqd-detail__row" style="font-weight:700;"><span>Commission</span><span>$0.00</span></div>'
        + '<div class="rqd-detail__linked-stub">A replacement is a $0 re-ship of goods already paid for. '
        + 'It earns no commission &mdash; and it takes none away: the original order&rsquo;s payout is '
        + 'settled on what it sold for.</div>'
        + '</div>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Column 3: Commission
    // Online orders skip the Commission card entirely — they don't
    // accrue commission unless a rep's discount code was used at
    // checkout (future: matched against rep.discountCodes[] in the
    // settings UI). Rest of the modes below apply to offline/rep quotes.
    // Three render modes for non-online:
    //   1. Rep earns commission → grid-driven Band + Rate + Commission $.
    //   2. Rep doesn't earn (e.g. RW) AND order is Shared/Referral with a
    //      named recipient → Band/Rate "n/a" + Commission line shows the
    //      referral amount + recipient name + GP%.
    //   3. Rep doesn't earn AND no referral → Band/Rate/Commission "n/a".
    // CUSTOM_RFQPORTAL_BY_CLAUDE — Linked children skip the Commission
    // card entirely. Commission is earned (or not) on the PARENT deal;
    // the child is just an accounting entry for the credit + balance
    // payment, so rendering Commission here would either duplicate the
    // parent's number or confuse admin about which row "owns" the comm.
    //
    // ALSO hide the card when the entire card would just read "n/a"
    // (rep doesn't earn AND no referral attached) — no signal worth
    // the panel real estate. Pre-compute the two flags so we can
    // short-circuit at the outer gate.
    var _commRepEarns = q.repEarnsCommission !== false;
    var _commHasReferral = !!q.referralName && parseFloat(q.referralAmount || 0) > 0;
    if (!(q.source !== 'online' && !q.linkedParentRef && (_commRepEarns || _commHasReferral))) return '';

    var html = '';
    var commGrid = {
      outbound: { 'Pay in Full': 0.37, '50/50': 0.37, '50/50 Net 30': 0.35, 'Net 30': 0.33 },
      inbound:  { 'Pay in Full': 0.27, '50/50': 0.27, '50/50 Net 30': 0.25, 'Net 30': 0.23 },
      shared:   { 'Pay in Full': 0.17, '50/50': 0.17, '50/50 Net 30': 0.15, 'Net 30': 0.13 }
    };
    var commRate = q.commissionRate || null;
    var commAmount = q.commissionAmount || null;
    var commLS = q.leadSource || null;
    var repEarns = q.repEarnsCommission !== false;
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Custom % of GP on the Workstation
       trumps the band-derived rate. When state.referral.gpPercent is
       set on a Shared/Referral order, that value IS the commission
       rate for this order — no Band line, no grid lookup. The named
       recipient gets the full commission. */
    var hasReferral = !!q.referralName && parseFloat(q.referralAmount || 0) > 0;
    var customGpPct = parseFloat(q.referralGpPercent) || 0;
    var useCustomRate = hasReferral && customGpPct > 0;
    if (useCustomRate) {
      commRate = customGpPct / 100;
      commAmount = parseFloat(q.referralAmount) || 0;
    } else if (repEarns && !commRate && commLS && q.terms && commGrid[commLS]) {
      commRate = commGrid[commLS][q.terms] || commGrid[commLS]['Net 30'] || null;
      if (commRate) {
        var gpCalc = fin.gpOfGoods || ((parseFloat(fin.directTotal) || 0) - (parseFloat(fin.costTotal) || 0));
        commAmount = Math.round(gpCalc * commRate * 100) / 100;
      }
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    html += '<div class="rqd-detail__card">';
    html += '<div class="rqd-detail__section-title">Commission</div>';
    html += detailRow('Lead Source', commLS ? capitalize(commLS) : '—');
    html += detailRow('Payment Terms', q.terms || '—');
    if (!repEarns) {
      // Rep doesn't earn — Band + Rate are not applicable. Commission line
      // surfaces the referral split when one is set, otherwise "n/a".
      html += detailRow('Commission Band', 'n/a');
      html += detailRow('Commission Rate', 'n/a');
      html += '<div class="rqd-detail__divider"></div>';
      var gpForCommNa = fin.gpOfGoods || ((parseFloat(fin.directTotal) || 0) - (parseFloat(fin.costTotal) || 0));
      html += '<div class="rqd-detail__row" style="font-weight:700;"><span>GP of Goods</span><span>' + money(gpForCommNa) + '</span></div>';
      html += '<div class="rqd-detail__divider"></div>';
      if (hasReferral) {
        var refPctStr = (parseFloat(q.referralGpPercent) || 0) + '% GP';
        html += '<div class="rqd-detail__row rqd-detail__row--green"><span>Commission (Referral)</span><span>' + money(q.referralAmount) + '</span></div>';
        html += detailRow('Recipient', esc(q.referralName) + ' (' + refPctStr + ')');
      } else {
        html += '<div class="rqd-detail__row"><span>Commission</span><span>n/a</span></div>';
      }
    } else if (commRate) {
      var pctStr = ((commRate * 100).toFixed(1).replace(/\.0$/, '')) + '%';
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Hide Band line when a custom
         referralGpPercent rate is in effect (admin set "% of GP"
         on the Workstation). The custom rate IS the rate; the
         Shared 13/15/17% band doesn't apply. */
      if (!useCustomRate) {
        var bandLabel = commLS === 'outbound' ? '33-37%' : commLS === 'inbound' ? '23-27%' : commLS === 'shared' ? '13-17%' : '';
        html += detailRow('Commission Band', bandLabel || '—');
      }
      html += detailRow('Commission Rate', pctStr + ' of GP');
      html += '<div class="rqd-detail__divider"></div>';
      var gpForComm = fin.gpOfGoods || ((parseFloat(fin.directTotal) || 0) - (parseFloat(fin.costTotal) || 0));
      html += '<div class="rqd-detail__row" style="font-weight:700;"><span>GP of Goods</span><span>' + money(gpForComm) + '</span></div>';
      html += '<div class="rqd-detail__divider"></div>';
      if (useCustomRate) {
        // Custom rate path: commission goes to the named referral recipient.
        html += '<div class="rqd-detail__row rqd-detail__row--green"><span>Commission (' + esc(q.referralName) + ')</span><span>' + money(commAmount) + '</span></div>';
      } else {
        html += '<div class="rqd-detail__row rqd-detail__row--green"><span>Commission</span><span>' + money(commAmount) + '</span></div>';
        // Surface referral split alongside the rep's cut on shared orders (only when not custom-rate).
        if (hasReferral) {
          var refPctStr2 = (parseFloat(q.referralGpPercent) || 0) + '% GP';
          html += '<div class="rqd-detail__row rqd-detail__row--green"><span>Referral (' + esc(q.referralName) + ')</span><span>' + money(q.referralAmount) + ' (' + refPctStr2 + ')</span></div>';
        }
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    } else {
      html += detailRow('Commission Rate', '—');
      html += detailRow('Commission', '—');
    }

    // CFA Commission (admin only)
    if (getIsAdmin()) {
      if (q.cfaCode) {
        html += '<div class="rqd-detail__divider"></div>';
        html += detailRow('CFA', q.cfaCode);
        html += detailRow('CFA Rate', '10% of GP');
        if (q.cfaCommissionAmount) {
          html += '<div class="rqd-detail__row rqd-detail__row--green"><span>CFA Commission</span><span>' + money(q.cfaCommissionAmount) + '</span></div>';
        }
        html += detailRow('Window Expires', q.cfaWindowExpires ? formatDate(q.cfaWindowExpires) : '—');
      } else {
        // No CFA on this quote — show "n/a", read-only. CFA attribution
        // is set in the Quote Workstation by whoever created the quote;
        // no need to surface an assign button here.
        html += '<div class="rqd-detail__divider"></div>';
        html += detailRow('CFA', 'n/a');
      }
    }

    html += '</div>';
    return html;
  }

  // -------------------------------------------------------------------
  // Commission Code Attribution — online orders attributed via discount code
  // -------------------------------------------------------------------
  function buildCommissionCodeAttribution(q) {
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Phase 3.5 Phase 1.5:
       Code Attribution Commission card. For online orders that were rep-
       attributed via a registered discount code at orders/paid time
       (q.attribution.method === 'discount-code'). Admin-only edit
       affordance — picks leadSource (outbound/inbound/shared); terms is
       already 'Pay in Full' at attribution time. On change, posts to
       /quote-lifecycle which recomputes commissionAmount via the
       lifecycle.financials fallback path. Card hidden for reps (they
       see SHOP-XXXX read-only on their Offline tab). */
    if (!(q.source === 'online' && q.attribution && q.attribution.method === 'discount-code')) return '';

    var html = '';
    var _attrFin = q.financials || {};
    var _attrGp = _attrFin.gpOfGoods != null
      ? _attrFin.gpOfGoods
      : ((parseFloat(_attrFin.directTotal) || 0) - (parseFloat(_attrFin.costTotal) || 0));
    var _attrGrid = {
      outbound: { 'Pay in Full': 0.37, '50/50': 0.37, '50/50 Net 30': 0.35, 'Net 30': 0.33 },
      inbound:  { 'Pay in Full': 0.27, '50/50': 0.27, '50/50 Net 30': 0.25, 'Net 30': 0.23 },
      shared:   { 'Pay in Full': 0.17, '50/50': 0.17, '50/50 Net 30': 0.15, 'Net 30': 0.13 }
    };
    var _attrLS = q.leadSource || '';
    var _attrTerms = q.terms || 'Pay in Full';
    var _attrRate = (_attrLS && _attrGrid[_attrLS]) ? (_attrGrid[_attrLS][_attrTerms] || _attrGrid[_attrLS]['Net 30']) : null;
    var _attrAmount = (_attrRate && _attrGp > 0) ? Math.round(_attrGp * _attrRate * 100) / 100 : 0;
    html += '<div class="rqd-detail__card">';
    html += '<div class="rqd-detail__section-title">Commission</div>';
    html += detailRow('Attributed To', esc(q.attribution.repCode || '—'));
    html += detailRow('Code Used', esc(q.attribution.code || '—'));
    html += detailRow('Payment Terms', esc(_attrTerms));
    // Lead source dropdown — single source of truth for which commission
    // band applies. Onchange handler at the dashboard root posts to
    // /quote-lifecycle and the next dashboard refresh shows the
    // recomputed commission + clears the ⚠ badge on the row.
    /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-19) — Admin on Offline
       tab sees the rep's read-only view (dropdown replaced with
       static text) since they'll manage from Online tab. Admin on
       Online/All tabs gets the editable dropdown. */
    if (getIsAdmin() && getCurrentSource() !== 'offline') {
      html += '<div class="rqd-detail__row"><span>Lead Source</span>'
        + '<select class="rqd-attr-leadsource" data-ref="' + esc(q.reference) + '">'
        + '<option value=""' + (_attrLS === '' ? ' selected' : '') + '>— pick one —</option>'
        + '<option value="outbound"' + (_attrLS === 'outbound' ? ' selected' : '') + '>Outbound (37%)</option>'
        + '<option value="inbound"' + (_attrLS === 'inbound' ? ' selected' : '') + '>Inbound (27%)</option>'
        + '<option value="shared"' + (_attrLS === 'shared' ? ' selected' : '') + '>Shared (17%)</option>'
        + '</select></span></div>';
    } else {
      // Reps see leadSource as static text — admin owns the choice.
      // Until admin sets it, show a friendly "Pending admin" so the rep
      // knows commission is awaiting setup (not broken).
      html += detailRow('Lead Source', _attrLS
        ? capitalize(_attrLS) + ' (' + (Math.round((_attrGrid[_attrLS]['Pay in Full']) * 1000) / 10) + '%)'
        : '<span style="color:var(--rq-text-muted, #6b7280);font-style:italic;">Pending admin</span>');
    }
    html += '<div class="rqd-detail__divider"></div>';
    html += '<div class="rqd-detail__row" style="font-weight:700;"><span>GP of Goods</span><span>' + money(_attrGp) + '</span></div>';
    if (_attrLS && _attrRate) {
      html += detailRow('Commission Band', capitalize(_attrLS));
      html += detailRow('Commission Rate', (Math.round(_attrRate * 1000) / 10) + '%');
      html += '<div class="rqd-detail__row rqd-detail__row--green" style="font-weight:700;"><span>Commission</span><span>' + money(_attrAmount) + '</span></div>';
    } else {
      html += '<div class="rqd-detail__row" style="color:var(--rq-text-muted, #6b7280);font-style:italic;"><span></span><span>Pick a lead source to compute commission</span></div>';
    }
    html += '</div>';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
    return html;
  }

  function initSampleCommission(deps) {
    deps = deps || {};
    esc = deps.esc;
    formatDate = deps.formatDate;
    money = deps.money;
    detailRow = deps.detailRow;
    capitalize = deps.capitalize;
    getIsAdmin = deps.getIsAdmin || function() { return false; };
    getCurrentSource = deps.getCurrentSource || function() { return ''; };
  }

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-13) — Delay / Backorder Notice card.
     Optional step for ONLINE orders only (admin-only). Rep selects the
     delayed/backordered line item(s) + an estimated ship time, then builds an
     editable email that Abby (orders@) sends. Mirrors the Sample Tracking card.
     Completion (q.delayNoticeSent, stamped by /notify-customer-delay) shows a
     ✓ meta line. The build/send wiring lives in dashboard-main.js.

     CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-31) — the card now sends TWO notices,
     picked by a mode switch: the delay/backorder email above, or a DISCONTINUED
     email for a product that is not coming back. Discontinued mode drops the ETA
     (meaningless for a dead product), offers a replacement picker per selected
     item, and shows which of the four discontinued emails it is about to send.

     ⛔ THE OUTCOME LINE IS NOT DECORATION. The email either promises a full refund
     or asks a question, and the difference is derived from how many of the order's
     items the rep ticked. A rep who cannot see that derivation before clicking Send
     is one stray checkbox away from telling a customer their whole order is
     cancelled, so the card states the ending in words. */
  function buildDelayNoticeCard(q, ctx) {
    if (!getIsAdmin()) return '';
    if (!q || q.source !== 'online') return '';
    var items = Array.isArray(q.onlineLineItems) ? q.onlineLineItems : [];
    if (!items.length) return '';
    var sent = q.delayNoticeSent || null;
    /* ⛔ THE SECTION ID IS DECLARED, NOT SLUGGED FROM THE HEADING. The heading gained
       "/ Discontinued" in 2026-08-31, and every card's collapse identity is otherwise
       the slug of its own title text — so the rename alone would have handed this card
       a brand new id, orphaning the shouldAutoCollapse rule that keeps it closed
       (dashboard-order-cards.js) and every rep's stored open/closed state with it.
       sectionIdFor() honours this attribute first, which is what that path is for. */
    var html = '<div class="rqd-detail__arap" data-section-id="delay-backorder-notice">';
    // Green ✓ on the header once the notice has been sent (same treatment as
    // the AR/AP, Purchasing, and Tracking cards) so the rep can see at a glance
    // that the email went out.
    /* ⛔ THE TICK MEANS SENT, NOT SCHEDULED. With the ⏱ business-hours box a notice can sit in
       the worker's queue until 6am, and the stamp for that state carries `scheduledFor` and no
       `at`. A green ✓ against an email still in a queue would be wrong on any card; on this one
       it is worse, because the discontinued email is the only record that a refund was promised
       and the tick would invite somebody to go issue it early. */
    var _sentAt = !!(sent && sent.at);
    html += (window.RQD && window.RQD.sectionTitleHtml)
      ? window.RQD.sectionTitleHtml('Delay / Backorder / Discontinued Notice', _sentAt)
      : '<div class="rqd-detail__section-title">Delay / Backorder / Discontinued Notice</div>';
    html += '<div class="rqd-delay-notice" data-ref="' + esc(q.reference) + '" data-mode="delay">';
    // Mode switch. Delay is checked by default, so an untouched card behaves
    // exactly as it did before this existed.
    var mn = 'rqd-dn-mode-' + esc(q.reference);
    html += '<div class="rqd-delay-notice__mode">'
      + '<label class="rqd-delay-notice__modeopt"><input type="radio" name="' + mn + '" class="rqd-delay-notice__moderadio" value="delay" checked> Delayed / backordered</label>'
      + '<label class="rqd-delay-notice__modeopt"><input type="radio" name="' + mn + '" class="rqd-delay-notice__moderadio" value="discontinued"> Discontinued</label>'
      + '</div>';
    html += '<div class="rqd-delay-notice__items">';
    // Single-item orders: pre-check the lone box so the rep doesn't have to
    // (the notice almost always covers the whole order). Count only items that
    // actually render a checkbox (title present) so a title-less line doesn't
    // throw off the "only one item" test.
    var autoCheck = items.filter(function (it) { return String(it.title || '').trim(); }).length === 1;
    var idx = 0;
    items.forEach(function (it) {
      var title = String(it.title || '').trim();
      if (!title) return;
      var label = esc(title) + (it.variantTitle ? ' <span class="rqd-online-items__variant">— ' + esc(it.variantTitle) + '</span>' : '');
      // Value = clean product name only (variant stripped) — the email/feed use
      // this; the label above still shows the variant for the rep's reference.
      var val = title.split(' - ')[0].trim();
      var brand = String(it.vendor || '').trim();
      var qty = (it.effectiveQuantity != null) ? it.effectiveQuantity : (it.quantity || 1);
      /* handle + sku ride along so the wiring can resolve this line's storefront URL
         through the SAME handle → SKU → exact-title ladder the email templates use
         (RQDTpl.linkForLine). A discontinued product often no longer resolves, and
         that is the correct answer: it renders unlinked rather than linking a 404. */
      html += '<div class="rqd-delay-notice__line" data-idx="' + idx + '">'
        + '<label class="rqd-delay-notice__item"><input type="checkbox" class="rqd-delay-notice__cb"' + (autoCheck ? ' checked' : '')
        + ' value="' + esc(val) + '" data-brand="' + esc(brand) + '" data-qty="' + esc(String(qty)) + '"'
        + ' data-handle="' + esc(String(it.productHandle || '')) + '" data-sku="' + esc(String(it.sku || '')) + '"'
        /* The variant the customer actually bought. It is the whole point of the usual
           discontinued case (Ryan: the vendor stopped making the mat in BLUE and still makes
           it in black), so the email names it on both sides of the swap. Stripped out of
           `value` above, which is why it needs its own attribute. */
        + ' data-variant="' + esc(String(it.variantTitle || '')) + '"> ' + label + '</label>'
        + '<div class="rqd-delay-notice__subs" hidden>'
        + '<div class="rqd-delay-notice__chips"></div>'
        + '<div class="rqd-delay-notice__subwrap">'
        + '<input type="text" class="rqd-delay-notice__subq" placeholder="Suggest a replacement (optional)" maxlength="80" autocomplete="off">'
        + '<div class="rqd-delay-notice__subhits" hidden></div>'
        + '</div></div></div>';
      idx++;
    });
    html += '</div>';
    html += '<div class="rqd-delay-notice__outcome" hidden></div>';
    html += '<div class="rqd-delay-notice__row">';
    html += '<input type="text" class="rqd-delay-notice__eta" placeholder="Est. ship — a date or &quot;week of June 23&quot;" maxlength="120">';
    html += '<button type="button" class="rqd-delay-notify rqd-invoice__link" data-ref="' + esc(q.reference) + '" title="Builds the notice and opens it for review before Abby sends.">Review / Send</button>';
    html += '</div>';
    if (sent) {
      var _kind = (sent.type === 'discontinued') ? 'Discontinued notice' : 'Notice';
      html += '<div class="rqd-sample-tracking__meta">'
        + (_sentAt
            ? _kind + ' sent' + (sent.by ? ' by ' + esc(sent.by) : '') + ' &middot; ' + esc(formatDate(sent.at) || sent.at)
            : _kind + ' scheduled'
              + (sent.scheduledFor ? ' for ' + esc(formatDate(sent.scheduledFor) || sent.scheduledFor) : '')
              + (sent.by ? ' by ' + esc(sent.by) : ''))
        + (sent.eta ? ' &middot; est. ship ' + esc(sent.eta) : '')
        + (sent.outcome ? ' &middot; ' + esc(sent.outcome) : '')
        + '</div>';
      /* CUSTOM_DISCDRIP_BY_CLAUDE (Ryan 2026-09-06) — one line saying Abby's follow-ups are live on this
         notice. `pendingAutomation` rides the row off the worker's ⚡ pass (the same field the Deal
         Feed's frozen block and the To Do badge read), so this costs nothing and cannot disagree
         with them; the copy, the countdown and Stop live on that block. */
      var _pa = q.pendingAutomation;
      if (_pa && _pa.flow === 'discontinued-drip') {
        var _next = _pa.sendAfter ? (formatDate(_pa.sendAfter) || _pa.sendAfter) : '';
        html += '<div class="rqd-sample-tracking__meta">&#9889; '
          + (_pa.closing
              ? 'Abby\u2019s follow-ups sent; Ryan is told to cancel and refund' + (_next ? ' ' + esc(_next) : '') + ' if nothing comes back'
              : 'Abby follow-up ' + esc(String(_pa.rung || 1)) + ' of ' + esc(String(_pa.of || 3)) + (_next ? ' goes ' + esc(_next) : '') + ' unless they reply')
          + ' &middot; details and Stop on the Deal Feed</div>';
      }
    }
    html += '</div></div>';
    return html;
  }
  /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

  window.RQD.buildSampleTrackingCard = buildSampleTrackingCard;
  window.RQD.buildDelayNoticeCard = buildDelayNoticeCard;
  window.RQD.buildCommissionCard = buildCommissionCard;
  window.RQD.buildCommissionCodeAttribution = buildCommissionCodeAttribution;
  window.RQD.initSampleCommission = initSampleCommission;
})();
